import renderDisplayDate from '@helpers/renderDisplayDate';
import useAPI from '@hooks/useAPI';
import useErrorHandler from '@hooks/useErrorHandler';
import useSession from '@hooks/useSession';
import Comment from '@models/Comment';
import Episode from '@models/Episode';
import User from '@models/User';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

function renderCommentContent(content: string) {
  content = content.replace(
    /<at id="(.*)>(.*)<\/at>/g,
    '<span style="color: #ffdd15">@$2</span>'
  );

  return (
    <p
      className="body-text mb8"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

interface CommentCardProps {
  episode: Episode;
  comment: Comment;
  onRefreshComments: () => Promise<void>;
}

function CommentCard(props: CommentCardProps) {
  const session = useSession();
  const API = useAPI();
  const errorHandler = useErrorHandler();
  const [isReplying, setReplying] = useState(false);
  const [replyInput, setReplyInput] = useState('');
  const { comment } = props;
  const router = useRouter();
  const [repliedUser, setRepliedUser] = useState<User | undefined>();

  function handleReplyInput(value: string) {
    if (!repliedUser) {
      setReplyInput(value);
      return;
    }

    if (value.startsWith('@' + repliedUser.display_name + ' ')) {
      setReplyInput(value.replace('@' + repliedUser.display_name + ' ', ''));
      return;
    }

    setRepliedUser(undefined);
    setReplyInput('');
  }

  function handleAddComment() {
    if (!repliedUser) {
      addComment(replyInput);
      return;
    }

    addComment(
      `<at id="${repliedUser.id}">${repliedUser.display_name}</at> ` +
        replyInput, comment.id
    );
  }

  async function addComment(content: string, replyCommentId?: string) {
    try {
      await API.addComments(props.episode.id, content, replyCommentId);
      await props.onRefreshComments();
    } catch (err) {
      errorHandler.handle(err);
    }
  }

  return (
    <div className="comment-card-component">
      <div className="reply-wrap">
        <div className="replier-avatar">
          <img
            src={comment.owner.avatar}
            loading="lazy"
            alt=""
            className="avatar-image"
          />
        </div>
        <div className="reply-info-wrap">
          <div className="replier-info-box">
            <p className="replier-name">{comment.owner.display_name}</p>
            <p className="reply-timecode">
              {renderDisplayDate(comment.created_at)}
            </p>
          </div>
          {renderCommentContent(comment.content)}

          {session.isLoggedIn ? (
            <div className="reply-button-wrap">
              <div className="icon-box-20 mr4">
                <img
                  src={'/icons/TopicDetailPage/reply.svg'}
                  loading="lazy"
                  alt=""
                />
              </div>
              <a
                className="text-link"
                onClick={() => {
                  setReplying(true);
                  setRepliedUser(comment.owner);
                }}
              >
                回覆留言
              </a>
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
      {comment.sub_comments?.map((subComment) => (
        <div key={subComment.id} className="reply-wrap ml64">
          <div className="my-comment-avatar">
            <img
              src={subComment.owner.avatar}
              loading="lazy"
              alt=""
              className="avatar-image"
            />
          </div>
          <div className="reply-info-wrap">
            <div className="replier-info-box">
              <p className="replier-name">{subComment.owner.display_name}</p>
              <p className="reply-timecode">
                {renderDisplayDate(subComment.created_at)}
              </p>
            </div>

            {renderCommentContent(subComment.content)}

            {session.isLoggedIn ? (
              <div className="reply-button-wrap">
                <div className="icon-box-20 mr4">
                  <img
                    src={'/icons/TopicDetailPage/reply.svg'}
                    loading="lazy"
                    alt=""
                  />
                </div>
                <a
                  className="text-link"
                  onClick={() => {
                    if (session.isLoggedIn) {
                      setReplying(true);
                      setRepliedUser(subComment.owner);
                    } else {
                      router.push('/login');
                    }
                  }}
                >
                  回覆留言
                </a>
              </div>
            ) : (
              <div />
            )}
          </div>
        </div>
      ))}
      {isReplying ? (
        <div className="_2nd-reply-wrap">
          <div className="my-comment-avatar">
            <img
              src={session.user?.avatar}
              loading="lazy"
              alt=""
              className="avatar-image"
            />
          </div>
          <div className="form-block w-form">
            <form className="my-comment-form">
              <input
                value={
                  (repliedUser ? '@' + repliedUser.display_name + ' ' : '') +
                  replyInput
                }
                onChange={(e) => handleReplyInput(e.target.value)}
                placeholder={
                  session.isLoggedIn ? '請寫下留言的內容' : '登入後即可留言'
                }
                className="my-comment-box w-input"
              />
              <a
                className="button comment-button w-button"
                onClick={() => {
                  session.isLoggedIn
                    ? handleAddComment()
                    : router.push('/login');
                }}
              >
                留言
              </a>
            </form>
          </div>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

export default CommentCard;
